<template>
  <!-- SearchableMultiSelectDropdown: draft multi-select with Clear + Apply -->
  <v-menu
    ref="menu"
    v-model="isMenuOpen"
    :close-on-content-click="false"
    offset-y
    transition="slide-y-transition"
    :disabled="disabled"
    :attach="resolvedAttach"
    :nudge-width="menuNudgeWidth"
    :max-width="resolvedMenuWidth"
    :min-width="resolvedMenuWidth"
    :content-class="resolvedMenuContentClass"
    :z-index="menuZIndex"
    :class="{ 'searchable-multi-select--field': isFieldVariant }"
    :data-testid="`${dataTestidPrefix}__menu`"
  >
    <template v-slot:activator="{ on, attrs }">
      <button
        type="button"
        class="searchable-multi-select__activator"
        :class="{
          'searchable-multi-select__activator--open': isMenuOpen,
          'searchable-multi-select__activator--disabled': disabled,
          'searchable-multi-select__activator--field': isFieldVariant,
          'searchable-multi-select__activator--has-chips': showChips && selectedCount,
        }"
        :style="activatorStyle"
        v-bind="attrs"
        v-on="on"
        :disabled="disabled"
        :data-testid="`${dataTestidPrefix}__activator`"
      >
        <template v-if="!isFieldVariant">
          <img
            v-if="isImageIcon"
            :src="icon"
            alt=""
            class="searchable-multi-select__activator-icon searchable-multi-select__activator-icon--img"
            :data-testid="`${dataTestidPrefix}__activator-icon`"
          />
          <v-icon
            v-else-if="icon"
            size="14"
            class="searchable-multi-select__activator-icon"
            :data-testid="`${dataTestidPrefix}__activator-icon`"
          >
            {{ icon }}
          </v-icon>
          <span
            v-if="label"
            class="searchable-multi-select__activator-label"
            :data-testid="`${dataTestidPrefix}__activator-label`"
          >
            {{ label }}
          </span>
        </template>
        <div
          v-if="showChips && selectedCount"
          class="searchable-multi-select__activator-chips"
          :data-testid="`${dataTestidPrefix}__activator-chips`"
        >
          <span
            v-for="chip in selectedChipItems"
            :key="chip.id"
            class="searchable-multi-select__selected-chip"
            :data-testid="`${dataTestidPrefix}__selected-chip-${chip.id}`"
          >
            <span
              class="searchable-multi-select__selected-chip-label"
              :title="chip.label"
            >
              {{ chip.label }}
            </span>
            <v-icon
              small
              class="searchable-multi-select__selected-chip-close"
              :data-testid="`${dataTestidPrefix}__selected-chip-close-${chip.id}`"
              @click.stop="removeChip(chip.id)"
            >
              mdi-close
            </v-icon>
          </span>
        </div>
        <span
          v-else
          class="searchable-multi-select__activator-value"
          :class="{ 'searchable-multi-select__activator-value--placeholder': !selectedCount }"
          :data-testid="`${dataTestidPrefix}__activator-value`"
        >
          {{ activatorDisplay }}
        </span>
        <v-icon
          size="16"
          class="searchable-multi-select__activator-chevron"
          :data-testid="`${dataTestidPrefix}__activator-chevron`"
        >
          {{ activatorChevronIcon }}
        </v-icon>
      </button>
    </template>

    <div
      class="searchable-multi-select__panel"
      :data-testid="`${dataTestidPrefix}__panel`"
    >
      <div
        class="searchable-multi-select__search-section"
        :data-testid="`${dataTestidPrefix}__search-container`"
      >
        <div class="searchable-multi-select__search-wrap">
          <lb-string
            label=""
            :placeholder="searchPlaceholder"
            v-model="searchTerm"
            :isClearable="true"
            hidedetails
            class="searchable-multi-select__search"
            :data-testid="`${dataTestidPrefix}__search`"
          />
          <img
            src="/img/icons/fbc-icons/SearchIcon.svg"
            alt=""
            class="searchable-multi-select__search-icon"
            :data-testid="`${dataTestidPrefix}__search-icon`"
          />
        </div>
      </div>

      <div
        class="searchable-multi-select__items-container"
        :data-testid="`${dataTestidPrefix}__items-container`"
      >
        <div
          v-if="!hasItems"
          class="searchable-multi-select__empty"
          :data-testid="`${dataTestidPrefix}__empty`"
        >
          {{ emptyMessage }}
        </div>

        <div
          v-else-if="showSelectAll && !isSearchActive"
          class="searchable-multi-select__item searchable-multi-select__item--all"
          :data-testid="`${dataTestidPrefix}__select-all-row`"
        >
          <v-checkbox
            :color="color"
            :ripple="false"
            class="searchable-multi-select__checkbox pa-0 ma-0"
            hide-details
            :input-value="isAllSelected"
            :indeterminate="isPartiallySelected"
            @change="toggleSelectAll"
            :data-testid="`${dataTestidPrefix}__select-all-checkbox`"
          >
            <template v-slot:label>
              <span
                class="searchable-multi-select__item-label searchable-multi-select__item-label--all"
                :data-testid="`${dataTestidPrefix}__select-all-label`"
              >
                {{ selectAllRowLabel }}
              </span>
            </template>
          </v-checkbox>
        </div>

        <div
          v-for="(item, index) in filteredItems"
          :key="`item-${getItemValue(item)}-${index}`"
          class="searchable-multi-select__item"
          :data-testid="`${dataTestidPrefix}__item-${getItemValue(item) || index}`"
        >
          <v-checkbox
            :color="color"
            :ripple="false"
            class="searchable-multi-select__checkbox pa-0 ma-0"
            hide-details
            :input-value="isDraftSelected(item)"
            @change="toggleItem(item)"
            :data-testid="`${dataTestidPrefix}__checkbox-${getItemValue(item) || index}`"
          >
            <template v-slot:label>
              <span
                class="searchable-multi-select__item-content"
                :data-testid="`${dataTestidPrefix}__item-label-${getItemValue(item) || index}`"
              >
                <span
                  class="searchable-multi-select__item-label"
                  :title="getItemText(item)"
                >
                  {{ getItemText(item) }}
                </span>
                <span
                  v-if="getItemSubText(item)"
                  class="searchable-multi-select__item-sublabel"
                  :title="getItemSubText(item)"
                >
                  {{ getItemSubText(item) }}
                </span>
              </span>
            </template>
          </v-checkbox>
        </div>
      </div>

      <div
        class="searchable-multi-select__footer"
        :data-testid="`${dataTestidPrefix}__actions`"
      >
        <button
          type="button"
          class="searchable-multi-select__footer-btn searchable-multi-select__footer-btn--clear secondaryBtn"
          @click="handleClear"
          :data-testid="`${dataTestidPrefix}__clear-btn`"
        >
          Clear
        </button>
        <button
          type="button"
          class="searchable-multi-select__footer-btn searchable-multi-select__footer-btn--apply primaryBtn"
          @click="handleApply"
          :data-testid="`${dataTestidPrefix}__apply-btn`"
        >
          Apply
        </button>
      </div>
    </div>
  </v-menu>
</template>

<script>
import { DIALOG_MENU_Z_INDEX, positionMenuNearActivator } from '@/utils/sync-attached-menu';
import { hideRawObjectIdLabel } from '@/modules/financialclose/utils/locationName';

/**
 * SearchableMultiSelectDropdown
 *
 * Draft multi-select menu (search + checkboxes + select-all).
 * Toolbar variant commits only on Apply (closing discards draft).
 * Field variant commits on Apply or when clicking outside.
 *
 * @example
 * <SearchableMultiSelectDropdown
 *   v-model="selectedLocations"
 *   :items="locationOptions"
 *   label="Location"
 *   icon="/img/icons/LocationIcon.svg"
 * />
 */
export default {
  name: 'SearchableMultiSelectDropdown',

  props: {
    value: {
      type: Array,
      default: () => [],
    },
    items: {
      type: Array,
      default: () => [],
    },
    label: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: 'All',
    },
    itemText: {
      type: String,
      default: 'label',
    },
    itemValue: {
      type: String,
      default: '_id',
    },
    /**
     * Optional item field for secondary line (e.g. role label under user name).
     */
    itemSubText: {
      type: String,
      default: '',
    },
    showSelectAll: {
      type: Boolean,
      default: true,
    },
    selectAllRowLabel: {
      type: String,
      default: 'All',
    },
    emptyMessage: {
      type: String,
      default: 'No data available to select',
    },
    searchPlaceholder: {
      type: String,
      default: 'Search',
    },
    menuWidth: {
      type: String,
      default: '280px',
    },
    maxActivatorWidth: {
      type: String,
      default: '160px',
    },
    dataTestidPrefix: {
      type: String,
      default: 'multi-select',
    },
    color: {
      type: String,
      default: 'caccounting',
    },
    icon: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * toolbar — compact filter chip (default)
     * field — full-width form field (Assignment Location)
     */
    variant: {
      type: String,
      default: 'toolbar',
      validator: (v) => ['toolbar', 'field'].includes(v),
    },
    /**
     * When true, closing the menu (e.g. click outside) commits draft selection.
     * Defaults to true for field variant, false for toolbar.
     */
    commitOnClose: {
      type: Boolean,
      default: undefined,
    },
    attach: {
      type: Boolean,
      default: true,
    },
    /**
     * Pin menu to the field with fixed positioning — use inside drawers/dialogs.
     */
    useFixedMenuPosition: {
      type: Boolean,
      default: false,
    },
    menuContentClass: {
      type: String,
      default: '',
    },
    menuZIndex: {
      type: Number,
      default: DIALOG_MENU_Z_INDEX,
    },
    showChips: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isMenuOpen: false,
      searchTerm: '',
      draftSelection: [],
      appliedOnClose: false,
      menuPinTimer: null,
    };
  },

  computed: {
    resolvedAttach() {
      return this.useFixedMenuPosition ? false : this.attach;
    },

    primaryMenuClass() {
      return this.menuContentClass || 'searchable-multi-select__menu-content';
    },

    resolvedMenuContentClass() {
      const classes = ['searchable-multi-select__menu-content'];
      if (this.menuContentClass) {
        classes.push(this.menuContentClass);
      }
      return classes.join(' ');
    },

    resolvedMenuWidth() {
      if (this.useFixedMenuPosition) return undefined;
      return this.menuWidth;
    },

    menuNudgeWidth() {
      return this.useFixedMenuPosition ? false : 0;
    },

    isFieldVariant() {
      return this.variant === 'field';
    },

    shouldCommitOnClose() {
      if (this.commitOnClose != null) return this.commitOnClose;
      return this.isFieldVariant;
    },

    activatorStyle() {
      if (this.isFieldVariant) {
        return { maxWidth: this.maxActivatorWidth || '100%', width: '100%' };
      }
      return { maxWidth: this.maxActivatorWidth };
    },

    isImageIcon() {
      if (!this.icon) return false;
      return (
        this.icon.startsWith('/') ||
        this.icon.startsWith('http') ||
        this.icon.startsWith('data:') ||
        /\.(svg|png|jpe?g|webp|gif)(\?.*)?$/i.test(this.icon)
      );
    },

    isSearchActive() {
      return Boolean(this.searchTerm?.trim());
    },

    hasItems() {
      return Array.isArray(this.items) && this.items.length > 0;
    },

    filteredItems() {
      if (!this.isSearchActive) {
        return this.items || [];
      }
      const term = this.searchTerm.toLowerCase().trim();
      return (this.items || []).filter((item) => {
        const text = this.getItemText(item) || '';
        const subText = this.getItemSubText(item) || '';
        return (
          String(text).toLowerCase().includes(term) ||
          String(subText).toLowerCase().includes(term)
        );
      });
    },

    selectedCount() {
      return (this.value || []).length;
    },

    selectedLabels() {
      if (!this.isFieldVariant) return [];
      const items = this.items || [];
      const textById = new Map(
        items.map((item) => [String(this.getItemValue(item)), this.getItemText(item)])
      );
      return (this.value || [])
        .map((id) => hideRawObjectIdLabel(textById.get(String(id)) ?? id))
        .filter((v) => v !== '');
    },

    selectedChipItems() {
      const items = this.items || [];
      const textById = new Map(
        items.map((item) => [String(this.getItemValue(item)), this.getItemText(item)])
      );
      return (this.value || [])
        .map((id) => ({
          id: String(id),
          label: hideRawObjectIdLabel(textById.get(String(id)) ?? id),
        }))
        .filter((chip) => chip.id && chip.label);
    },

    activatorChevronIcon() {
      if (this.isFieldVariant && this.showChips) return 'mdi-chevron-down';
      if (this.isFieldVariant) return 'mdi-magnify';
      return 'mdi-chevron-down';
    },

    activatorDisplay() {
      const selected = this.value || [];
      if (selected.length === 0) {
        return this.placeholder || (this.isFieldVariant ? 'Select Location' : 'All');
      }
      if (this.isFieldVariant) {
        return this.selectedLabels.join(', ');
      }
      if (selected.length === 1) {
        const match = (this.items || []).find(
          (item) => this.getItemValue(item) === selected[0]
        );
        return match ? this.getItemText(match) : hideRawObjectIdLabel(selected[0]);
      }
      return `${selected.length} selected`;
    },

    isAllSelected() {
      const total = (this.items || []).length;
      if (total === 0) return false;
      return this.draftSelection.length === total;
    },

    isPartiallySelected() {
      const total = (this.items || []).length;
      const selected = this.draftSelection.length;
      return selected > 0 && selected < total;
    },
  },

  watch: {
    isMenuOpen(isOpen) {
      if (isOpen) {
        this.appliedOnClose = false;
        this.syncDraftFromValue();
        this.searchTerm = '';
        if (this.useFixedMenuPosition) {
          this.$nextTick(() => {
            requestAnimationFrame(() => {
              this.pinMenuToActivator();
              if (this.menuPinTimer) window.clearTimeout(this.menuPinTimer);
              this.menuPinTimer = window.setTimeout(() => {
                this.menuPinTimer = null;
                if (!this.isMenuOpen) return;
                this.pinMenuToActivator();
              }, 50);
            });
          });
        }
      } else {
        if (this.menuPinTimer) {
          window.clearTimeout(this.menuPinTimer);
          this.menuPinTimer = null;
        }
        this.searchTerm = '';
        if (!this.appliedOnClose) {
          if (this.shouldCommitOnClose) {
            this.$emit('input', [...this.draftSelection]);
          } else {
            this.syncDraftFromValue();
            this.$emit('cancel');
          }
        }
        this.appliedOnClose = false;
      }
    },

    value: {
      deep: true,
      handler() {
        if (!this.isMenuOpen) {
          this.syncDraftFromValue();
          return;
        }
        if (this.useFixedMenuPosition) {
          this.refreshMenuPosition();
        }
      },
    },

    searchTerm() {
      if (this.isMenuOpen && this.useFixedMenuPosition) {
        this.refreshMenuPosition();
      }
    },
  },

  beforeDestroy() {
    if (this.menuPinTimer) {
      window.clearTimeout(this.menuPinTimer);
      this.menuPinTimer = null;
    }
  },

  methods: {
    refreshMenuPosition() {
      if (!this.useFixedMenuPosition || !this.isMenuOpen) return;
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          this.pinMenuToActivator();
        });
      });
    },

    pinMenuToActivator() {
      if (!this.useFixedMenuPosition || !this.isMenuOpen) return;
      const activatorEl = this.$el?.querySelector('.searchable-multi-select__activator');
      if (!activatorEl) return;

      positionMenuNearActivator({
        activatorEl,
        menuClass: this.primaryMenuClass,
        fieldRef: this.$refs.menu,
        maxMenuHeight: 320,
      });
    },
    getItemValue(item) {
      if (item == null) return undefined;
      if (typeof item !== 'object') return item;
      return item[this.itemValue];
    },

    getItemText(item) {
      if (item == null) return '';
      if (typeof item !== 'object') return String(item);
      return item[this.itemText] ?? item.name ?? item.label ?? String(this.getItemValue(item));
    },

    getItemSubText(item) {
      if (!this.itemSubText || item == null || typeof item !== 'object') {
        return '';
      }
      const value = item[this.itemSubText];
      return value == null || value === '' ? '' : String(value);
    },

    syncDraftFromValue() {
      this.draftSelection = Array.isArray(this.value)
        ? this.value.map((id) => String(id))
        : [];
    },

    isDraftSelected(item) {
      const id = String(this.getItemValue(item) ?? '');
      return (this.draftSelection || []).some((selected) => String(selected) === id);
    },

    toggleItem(item) {
      const id = String(this.getItemValue(item) ?? '');
      const index = (this.draftSelection || []).findIndex((selected) => String(selected) === id);
      if (index > -1) {
        this.draftSelection.splice(index, 1);
      } else if (id) {
        this.draftSelection.push(id);
      }
    },

    toggleSelectAll() {
      if (this.isAllSelected) {
        this.draftSelection = [];
      } else {
        this.draftSelection = (this.items || [])
          .map((item) => String(this.getItemValue(item) ?? ''))
          .filter(Boolean);
      }
    },

    handleClear() {
      this.draftSelection = [];
    },

    handleApply() {
      this.appliedOnClose = true;
      this.$emit('input', [...this.draftSelection]);
      this.isMenuOpen = false;
    },

    removeChip(id) {
      const next = (this.value || []).filter((value) => String(value) !== String(id));
      this.$emit('input', next);
    },
  },
};
</script>

<style lang="scss" scoped>
@import './SearchableMultiSelectDropdown.scss';
</style>

<style lang="scss">
$searchable-multi-select-teleported-only: true;
@import './SearchableMultiSelectDropdown.scss';
</style>

